<script>
	import OpenSeadragon from "openseadragon";
	import Annotorious from "@recogito/annotorious-openseadragon";
	// import TiltedBox from "@recogito/annotorious-tilted-box/dist/annotorious-tilted-box.min.js";
	import { setFilterOptions } from "openseadragon-filtering";
	import { createWorker } from "tesseract.js/dist/tesseract.min.js";
	import { SvelteToast, toast } from "@zerodevx/svelte-toast"

	import Icon from "./Icon.svelte";
	import Modal from "./Modal.svelte"
	import Languages from "./Languages.svelte"
	import Help from "./Help.svelte"
	import Info from "./Info.svelte"
	import { languages, modal } from "./stores.js"
	import { onMount, tick } from "svelte";

	const iconSize = "1.2em"
	const strokeWidth = "1.5px"

	let viewer;
	let anno;
	let worker;

	let sourceType = "tiles"
	let sourceURL = "https://stacks.stanford.edu/image/iiif/ts216hh0788/36105116273652_00_0038/info.json";
	let lastURL = sourceURL
	let lastType = sourceType

	let filters = [
		{
			name: "Threshold",
			enabled: false,
			value: 0,
			minValue: 0,
			maxValue: 255,
			get processor() { return OpenSeadragon.Filters.THRESHOLDING(this.value) }
		},
		{
			name: "Invert",
			enabled: false,
			get processor() { return OpenSeadragon.Filters.INVERT() }
		}
	]

	let snippet;
	let ocr = "";
	let ocrDom;
	let disabled = false;

	let showModal;

	const toggleModal = () => showModal = !showModal;
	const setModal = m => $modal = m;

	function handleModal(m) {
		setModal(m)
		toggleModal();
	}

	onMount(async () => {
		viewer = OpenSeadragon({
			id: "osd",
			zoomInButton: "zoom-in",
    		zoomOutButton: "zoom-out",
    		homeButton: "home",
			rotateLeftButton: "rotate-left",
			rotateRightButton: "rotate-right",
    		showFullPageControl: false,
			showRotationControl: true,
			crossOriginPolicy: "Anonymous"
    	});

		changeTileSource(sourceType, sourceURL)

		anno = Annotorious(viewer, { disableEditor: true });
		// TiltedBox(anno);
	});

	function handleSuccess(_e) {
		lastURL = sourceURL
		lastType = sourceType
	}

	function handleError(e) {
		toast.push(`${e.message}`, {
			theme: {
				'--toastBackground': '#F56565',
				'--toastProgressBackground': '#C53030',
				'--toastWidth': `${e.message.length / 1.5}rem`
				}
			}
		)
		sourceURL = lastURL
		sourceType = lastType
		changeTileSource(lastType, lastURL)
	}

	function changeTileSource(type, url) {

		viewer.close()

		if (type === "image") {
			viewer.open({
				tileSource: {
					type: "image",
					url: url
				},
				success: handleSuccess,
				error: handleError
			})
		} else {
			viewer.open({
				tileSource: url,
				success: handleSuccess,
				error: handleError
			})
		}

	}

	async function ocrSelection() {
		try {
			({ snippet } = anno.getSelectedImageSnippet());
		} catch(e) {
			toast.push("Select an area of the image (hold SHIFT and drag).")
			return
		}

		if ($languages.length === 0) {
			toast.push("Select one to three languages.")
			return
		}

		const selectedLanguages = $languages.join("+")

		disabled = true;

		const t = toast.push('Loading...', {
			initial: 0,
			progress: 1,
			dismissable: false
		})
	
		worker = createWorker({
			langPath: "https://raw.githubusercontent.com/naptha/tessdata/gh-pages/4.0.0_fast",
			logger: m => {
				toast.set(t, { msg: m.status, progress: 1-Math.round(m.progress) })
			}
		});
		await worker.load();
		await worker.loadLanguage(selectedLanguages);
  		await worker.initialize(selectedLanguages);
		const { data: { text } } = await worker.recognize(snippet);
		ocr += text + "\n"
		disabled = false;
	}

	// function toggleTiltedBox() {
	// 	anno.setDrawingTool("annotorious-tilted-box");
	// }

	function setFilter() {
		const enabled = filters.filter(x => x.enabled)
		console.log("Enabled", enabled)
		console.log("Processors", enabled.map(x => x.processor))
		viewer.setFilterOptions({
			filters: {
				processors: enabled.map(x => x.processor)
			}
		});
	}

	async function copy() {
		if (!ocr.trim()) {
			toast.push("Nothing to copy")
			return
		}
		await tick();
		ocrDom.focus()
		ocrDom.select()
		try {
			const success = document.execCommand("copy");
			if (success) {
				toast.push("Copied")
			} else {
				toast.push("Couldn't copy")
			}
		} catch(err) {
			toast.push(`Error copying: ${err}`)
		}
	}

	function download() {
		if (!ocr.trim()) {
			toast.push("Nothing to download")
			return
		}
		window.open("data:application/txt," + encodeURIComponent(ocr), "_self");
	}

</script>

<Modal {showModal} on:click={toggleModal}>
	{#if $modal === "languages"}
		<Languages />
	{:else if $modal === "help"}
		<Help />
	{:else if $modal === "info"}
		<Info />
	{/if}
</Modal>

<svelte:head>
   {#if showModal}
      <style>
         body {
            overflow: hidden;
         }
      </style>
   {/if}
</svelte:head>	

<main>
	<div class="help">
		<button on:click={() => handleModal("help")}>
			<Icon name="help-circle" width={iconSize} height={iconSize} {strokeWidth} />
			Help
		</button>
		<button on:click={() => handleModal("info")}>
			<Icon name="info" width={iconSize} height={iconSize} {strokeWidth} />
			Info
		</button>
	</div>

	<h1>OCR</h1>

	<SvelteToast />

	<form on:submit|preventDefault={() => changeTileSource(sourceType, sourceURL)}>
		<select bind:value={sourceType}>
			<option value="image">Single</option>
			<option value="tiles">Tiles</option>
		</select>
		<input type="url" placeholder="Enter a URL" bind:value={sourceURL} required>
		<button type="submit">Change</button>
		<!-- <strong>or</strong>
		<label for="file-upload" class="upload">Upload</label>
		<input type="file" id="file-upload" name="filename" multiple=false> -->
	</form>

	<div class="workspace">
		<div class="viewer">
			<div class="tools">
				<button id="zoom-in">
					<Icon name="zoom-in" width={iconSize} height={iconSize} {strokeWidth} />
				</button>
				<button id="zoom-out">
					<Icon name="zoom-out" width={iconSize} height={iconSize} {strokeWidth}/>
				</button>
				<button id="home">
					<Icon name="maximize" width={iconSize} height={iconSize} {strokeWidth}/>
				</button>
				<button id="rotate-left">
					<Icon name="rotate-ccw" width={iconSize} height={iconSize} {strokeWidth}/>
				</button>
				<button id="rotate-right">
					<Icon name="rotate-cw" width={iconSize} height={iconSize} {strokeWidth}/>
				</button>

				<button on:click={anno.cancelSelected}>
					<Icon name="trash" width={iconSize} height={iconSize} {strokeWidth}/>
					Clear selection
				</button>
				<button on:click={ocrSelection} {disabled}>
					{#if disabled}
						<div class="loader"></div>
					{:else}
						<Icon name="play" width={iconSize} height={iconSize} {strokeWidth}/>
					{/if}
					OCR
				</button>
			</div>

			<div id="osd"></div>

			<div class="tools">
				<button on:click={() => handleModal("languages")}>
					Languages
						{#if $languages.length > 0}
							({$languages.join("+")})
						{:else}
							(none)
						{/if}
				</button>
				{#each filters as f}
					<div class="tool">
						<button on:click={() => {
							f.enabled = !f.enabled;
							setFilter()
							}
						}
						>
						{#if f.enabled}
							<Icon name="minus-circle" width={iconSize} height={iconSize} {strokeWidth}/>
						{:else}
							<Icon name="plus-circle" width={iconSize} height={iconSize} {strokeWidth}/>
						{/if}
						{f.name}
						</button>
						{#if f.enabled && f.hasOwnProperty("value")}
							<input type="range" min={f.minValue} max={f.maxValue} bind:value={f.value} on:change={setFilter}>
						{/if}
					</div>
				{/each}
			</div>

		</div>
		<div class="editor">
			<div class="tools">
				<button on:click={copy}><Icon name="clipboard" width={iconSize} height={iconSize} {strokeWidth}/>Copy</button>
				<button on:click={download}><Icon name="download" width={iconSize} height={iconSize} {strokeWidth} />Download</button>
			</div>
			<form>
				<textarea aria-label="Result" bind:this={ocrDom} bind:value={ocr}></textarea>
			</form>
		</div>
	</div>
</main>

<style>
	/* @import '@recogito/annotorious-openseadragon/dist/annotorious.min.css'; */

	main {
		padding: 1em;
	}
	.workspace {
		display: grid;
		grid-template-columns: repeat(1, minmax(0, 1fr));
		gap: 1em;
	}

	@media (min-width: 1156px) {
		.workspace {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}

	h1 {
		margin-top: 0;
	}

	form {
		display: flex;
		flex-wrap: wrap;
		margin-bottom: 1em;
	}

	/* form strong {
		margin: 0.8em 1.2em 1em 1em;
	}

	input[type="file"] {
		display: none;
	} */

	textarea {
		box-sizing: border-box;
		height: 64vh;
	}

	/* .upload {
		display: block;
		cursor: pointer;
		color: var(--form-text);
		background-color: var(--background);
		border-radius: 6px;
		padding: 10px 30px;
		margin-bottom: 6px;
		-webkit-appearance: none;
		transition: background-color var(--animation-duration) linear,border-color var(--animation-duration) linear,color var(--animation-duration) linear,box-shadow var(--animation-duration) linear,transform var(--animation-duration) ease;
	}

	.upload:hover {
		background-color: var(--button-hover)
	}

	.upload:focus {
		box-shadow: 0 0 0 2px var(--focus);
	} */

	.tools {
		margin-bottom: 0.2em;
		display: flex;
		flex-wrap: wrap;
		align-items: center;
	}

	.tool {
		display: flex;
	}
	.tool input {
		margin-top: 0;
		margin-bottom: 0;
		margin-right: 0.6em;
	}

	.help {
		position: absolute;
		display: flex;
		right: 0.6em;
	}

	#osd {
		width: 100%;
		height: 64vh;
		background-color: var(--background);
		border-radius: 6px;
		margin-bottom: 0.7em;
	}

</style>